import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import type {
  CatalogueEntrySummary,
  CatalogueTemplateGroup,
  GuardianRule,
  RuleOutcomeCounts,
  RuleSetResponse,
} from "@/services/api/guardiansv2/types";
import { guardiansErrorMessage } from "../caughtError";

function rulesEqual(left: GuardianRule[], right: GuardianRule[]): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export interface UseRulesResult {
  catalogue: CatalogueEntrySummary[];
  catalogueGroups: CatalogueTemplateGroup[];
  ungroupedCatalogue: CatalogueEntrySummary[];
  catalogueKey: string | null;
  catalogueClientId: string | undefined;
  setCatalogueKey: (key: string) => void;
  rules: GuardianRule[];
  ruleStats: Record<string, RuleOutcomeCounts>;
  canEditRules: boolean;
  setRules: (rules: GuardianRule[] | ((prev: GuardianRule[]) => GuardianRule[])) => void;
  isDefault: boolean;
  isDirty: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  save: () => Promise<boolean>;
  reset: () => Promise<void>;
  reload: () => Promise<void>;
}

function clientIdForCatalogueKey(
  key: string,
  catalogue: CatalogueEntrySummary[],
  groups: CatalogueTemplateGroup[],
  ungrouped: CatalogueEntrySummary[],
): string | undefined {
  const missionScopes = groups.map((group) => group.mission_scope).filter(Boolean) as CatalogueEntrySummary[];
  const entry = [...catalogue, ...ungrouped, ...groups.flatMap((group) => group.entries), ...missionScopes].find(
    (item) => item.key === key,
  );
  return entry?.client_id;
}

export function useRules(enabled: boolean): UseRulesResult {
  const [catalogue, setCatalogue] = useState<CatalogueEntrySummary[]>([]);
  const [catalogueGroups, setCatalogueGroups] = useState<CatalogueTemplateGroup[]>([]);
  const [ungroupedCatalogue, setUngroupedCatalogue] = useState<CatalogueEntrySummary[]>([]);
  const [catalogueKey, setCatalogueKeyState] = useState<string | null>(null);
  const [rules, setRulesState] = useState<GuardianRule[]>([]);
  const [ruleStats, setRuleStats] = useState<Record<string, RuleOutcomeCounts>>({});
  const [canEditRules, setCanEditRules] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const draftsByKeyRef = useRef<Record<string, GuardianRule[]>>({});
  const savedByKeyRef = useRef<Record<string, GuardianRule[]>>({});
  const isDefaultByKeyRef = useRef<Record<string, boolean>>({});

  const setRules = useCallback(
    (update: GuardianRule[] | ((prev: GuardianRule[]) => GuardianRule[])) => {
      setRulesState((prev) => {
        const next = typeof update === "function" ? update(prev) : update;
        if (catalogueKey) {
          draftsByKeyRef.current[catalogueKey] = next;
        }
        return next;
      });
    },
    [catalogueKey]
  );

  const setCatalogueKey = useCallback((key: string) => {
    setCatalogueKeyState(key);
  }, []);

  const loadCatalogue = useCallback(async () => {
    const result = await GuardiansV2API.listRulesCatalogue();
    setCatalogue(result.catalogue);
    setCatalogueGroups(result.groups ?? []);
    setUngroupedCatalogue(result.ungrouped ?? []);
    setCanEditRules(result.can_edit_rules === true);
    setCatalogueKeyState((prev) => prev ?? result.catalogue[0]?.key ?? null);
  }, []);

  const applyRuleSet = useCallback((key: string, ruleSet: RuleSetResponse, draftRules: GuardianRule[]) => {
    savedByKeyRef.current[key] = ruleSet.rules;
    isDefaultByKeyRef.current[key] = Boolean(ruleSet.is_default);
    draftsByKeyRef.current[key] = draftRules;
    setRulesState(draftRules);
    setIsDefault(Boolean(ruleSet.is_default));
    setCanEditRules(ruleSet.can_edit_rules === true);
  }, []);

  const clientIdForKey = useCallback(
    (key: string) => clientIdForCatalogueKey(key, catalogue, catalogueGroups, ungroupedCatalogue),
    [catalogue, catalogueGroups, ungroupedCatalogue],
  );

  const loadRulesForKey = useCallback(
    async (key: string) => {
      setLoading(true);
      setError(null);
      try {
        const draft = draftsByKeyRef.current[key];
        const clientId = clientIdForKey(key);
        const ruleSet = await GuardiansV2API.getRules(key, clientId);
        const nextDraft = draft ?? ruleSet.rules;
        applyRuleSet(key, ruleSet, nextDraft);
      } catch (err) {
        setError(guardiansErrorMessage(err, "Failed to load rules"));
      } finally {
        setLoading(false);
      }
    },
    [applyRuleSet, clientIdForKey]
  );

  useEffect(() => {
    if (!enabled) return;
    void loadCatalogue().catch((err) => setError(guardiansErrorMessage(err, "Failed to load catalogue")));
  }, [enabled, loadCatalogue]);

  useEffect(() => {
    if (!enabled || !catalogueKey) return;
    const draft = draftsByKeyRef.current[catalogueKey];
    const saved = savedByKeyRef.current[catalogueKey];
    if (draft && saved) {
      setRulesState(draft);
      setIsDefault(isDefaultByKeyRef.current[catalogueKey] ?? true);
      return;
    }
    void loadRulesForKey(catalogueKey);
  }, [enabled, catalogueKey, loadRulesForKey]);

  useEffect(() => {
    if (!enabled || !catalogueKey) {
      setRuleStats({});
      return;
    }
    let cancelled = false;
    void GuardiansV2API.getRuleStats(catalogueKey, 100, clientIdForKey(catalogueKey))
      .then((res) => {
        if (!cancelled) setRuleStats(res.rules);
      })
      .catch(() => {
        if (!cancelled) setRuleStats({});
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, catalogueKey, clientIdForKey]);

  const catalogueClientId = useMemo(
    () => (catalogueKey ? clientIdForKey(catalogueKey) : undefined),
    [catalogueKey, clientIdForKey],
  );

  const isDirty = useMemo(() => {
    if (!catalogueKey) return false;
    const saved = savedByKeyRef.current[catalogueKey];
    if (!saved) return false;
    const draft = draftsByKeyRef.current[catalogueKey] ?? rules;
    return !rulesEqual(draft, saved);
  }, [catalogueKey, rules]);

  const save = useCallback(async (): Promise<boolean> => {
    if (!catalogueKey || !isDirty || !canEditRules) return false;
    setSaving(true);
    setError(null);
    try {
      const entry = catalogue.find((item) => item.key === catalogueKey);
      const draft = draftsByKeyRef.current[catalogueKey] ?? rules;
      const clientId = clientIdForKey(catalogueKey);
      const saved = await GuardiansV2API.putRules(
        catalogueKey,
        {
          rules: draft,
          catalogue_display_name: entry?.display_name,
        },
        clientId,
      );
      savedByKeyRef.current[catalogueKey] = saved.rules;
      draftsByKeyRef.current[catalogueKey] = saved.rules;
      isDefaultByKeyRef.current[catalogueKey] = false;
      setRulesState(saved.rules);
      setIsDefault(false);
      return true;
    } catch (err) {
      setError(guardiansErrorMessage(err, "Failed to save rules"));
      return false;
    } finally {
      setSaving(false);
    }
  }, [canEditRules, catalogue, catalogueKey, clientIdForKey, isDirty, rules]);

  const reset = useCallback(async () => {
    if (!catalogueKey) return;
    const saved = savedByKeyRef.current[catalogueKey];
    if (!saved) {
      await loadRulesForKey(catalogueKey);
      return;
    }
    draftsByKeyRef.current[catalogueKey] = saved;
    setRulesState(saved);
    setIsDefault(isDefaultByKeyRef.current[catalogueKey] ?? false);
  }, [catalogueKey, loadRulesForKey]);

  return {
    catalogue,
    catalogueGroups,
    ungroupedCatalogue,
    catalogueKey,
    catalogueClientId,
    setCatalogueKey,
    rules,
    ruleStats,
    canEditRules,
    setRules,
    isDefault,
    isDirty,
    loading,
    saving,
    error,
    save,
    reset,
    reload: () => (catalogueKey ? loadRulesForKey(catalogueKey) : Promise.resolve()),
  };
}
